/**
 * Store: Index
 * -----------------------------------------------------------------------------
 * Root store for all theme level states.
 * - Tied to the index component.
 * - Only JSDoc custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';
import useCartStore from '~stores/cart';

const useIndexStore = defineStore('index', {
  state: () => ({
    hydrated: false,
    notification: false,
    previousUrl: '/',
    geolocationCountry: {
      code: false,
      name: false,
    },
    geolocationLanguage: false,
    screen: {
      breakpoint: 'xs',
      height: 0,
      mobile: true,
      width: 0,
    },
    scroll: {
      direction: 'none',
      header: 'open',
      paused: false,
      top: true,
      x: 0,
      y: 0,
    },
    stickyCta: {
      sticky: false,
      visible: false,
    },
    stickyCtaBlockers: [],
    settings: {
      mobileBreakpoints: ['xs', 's', 'm'],
      rtlLanguages: [
        'ar',
        'dv',
        'fa',
        'ff',
        'he',
        'ks',
        'ku',
        'ur',
        'wo',
        'yi',
      ],
      start: 100,
      threshold: 5,
      throttle: 250,
    },
    stylesheets: [],
    mobileNavActive: false,
    // Bundle metaobject components for the current PDP's product (see
    // main-product.vue), shared here so other independently-mounted
    // sections on the same page (e.g. bought-together) can render the same
    // bundle-aware variant picker without re-fetching it themselves. `null`
    // when the current product isn't a bundle.
    productBundle: null,
  }),

  getters: {
    /**
     * Get if stylesheet is in stylesheets.
     * - Does not need to be complete match to be true.
     * @param {String} stylesheet - Stylesheet to test.
     * @returns {Boolean}
     */
    getLoadedStylesheet: (state) => (stylesheet) => {
      return state.stylesheets.some((loadedStylesheet) => {
        return loadedStylesheet.includes(stylesheet);
      });
    },

    /**
     * Get if RTL writing direction.
     * @returns {Boolean}
     */
    getRtlWritingDirection(state) {
      return state.settings.rtlLanguages.includes(cnvs.store.language);
    },

    getGeolocationCountry(state) {
      return state.geolocationCountry;
    },

    getGeolocationLanguage(state) {
      return state.geolocationLanguage;
    },

    /**
     * Get if the sticky CTA should be visible.
     * - Hidden whenever a section (e.g. Bought Together, Product Family)
     *   blocks it while in view, regardless of the requested visibility.
     * @returns {Boolean}
     */
    getStickyCtaVisible(state) {
      return (
        state.stickyCta.sticky &&
        state.stickyCta.visible &&
        !state.stickyCtaBlockers.length
      );
    },
  },

  actions: {
    /**
     * Commit rehydrated state.
     * - Runs initial items.
     * - set hydrated after setResponse to always have newest cart
     */
    async rehydrateSite() {
      const cartStore = useCartStore();

      await Promise.all([cartStore.setResponse(true)]);
      this.hydrated = true;
    },

    /**
     * Commit mobile breakpoints state.
     * @param {Array} mobileBreakpoints - Breakpoints considered as mobile.
     */
    setMobileBreakpoints(mobileBreakpoints) {
      this.settings.mobileBreakpoints = mobileBreakpoints;
    },

    /**
     * Commit notification state.
     * @param {Boolean|Object} notification - Notification to display, see
     * notification-panel.vue for props, accepts false to hide.
     */
    setNotification(notification = {}) {
      this.notification = notification;
    },

    /**
     * Commit previous URL state.
     * @param {String} pathname - Previous pathname.
     */
    setPreviousUrl(pathname) {
      this.previousUrl = pathname;
    },

    /**
     * Commit screen state.
     * @param {Object} screen - Screen state object.
     */
    setScreen(screen) {
      this.screen = screen;
    },

    /**
     * Set scroll state.
     * - Does not update paused state.
     * @param {Object} scroll - Scroll state object.
     */
    setScroll(scroll) {
      this.scroll = {
        ...scroll,
        paused: this.scroll.paused,
      };
    },

    /**
     * Set scroll start.
     * @param {String} start - New scroll start.
     */
    setScrollStart(start) {
      this.settings.start = start;
    },

    /**
     * Commit loaded stylesheet state.
     * @param {String} stylesheet - Loaded stylesheet file name.
     */
    setStylesheet(stylesheet) {
      this.stylesheets = [...this.stylesheets, stylesheet];
    },

    /**
     *
     * @param {String} country - country handle as returned by
     * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
     */
    setGeolocationCountry(country) {
      this.geolocationCountry = country;
    },

    /**
     *
     * @param {String} lang - lang handle as returned by
     * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
     */
    setGeolocationLanguage(lang) {
      this.geolocationLanguage = lang;
    },

    setStickyCta(state) {
      this.stickyCta = state;
    },

    /**
     * Commit sticky CTA blocker state.
     * - Used by sections that need to hide the sticky CTA while in view,
     *   without touching the requested sticky/visible state so it's
     *   restored as-is once no blockers remain.
     * @param {String} id - Unique blocker id, e.g. a section id.
     * @param {Boolean} isBlocking - Whether this id should block the CTA.
     */
    setStickyCtaBlocker(id, isBlocking) {
      const hasBlocker = this.stickyCtaBlockers.includes(id);

      if (isBlocking && !hasBlocker) {
        this.stickyCtaBlockers = [...this.stickyCtaBlockers, id];
      } else if (!isBlocking && hasBlocker) {
        this.stickyCtaBlockers = this.stickyCtaBlockers.filter(
          (blockerId) => blockerId !== id,
        );
      }
    },

    setMobileNavActive(state) {
      this.mobileNavActive = state;
      cnvs.EventBus.emit('index:setMobileNavActive', state);
    },

    /**
     * Commit the current PDP's bundle metaobject components, see
     * `productBundle` state.
     * @param {Array|null} productBundle - Bundle components, or `null`.
     */
    setProductBundle(productBundle) {
      this.productBundle = productBundle;
    },
  },
  persist: {
    pick: ['notifications', 'geolocationCountry', 'geolocationLanguage'],
  },
});

export default useIndexStore;
